import React from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  IconButton,
  Tooltip,
  Button,
  InputAdornment,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";

/**
 * The amount-wise interest ladder editor — the body of the Interest Slab
 * section when a store prices on loan SIZE rather than on elapsed months.
 *
 * Three plain fields per band: From, To, Interest %. Both bounds are inclusive
 * and typed by the user; a blank To means no upper limit.
 *
 * ── Why nothing is derived or locked ────────────────────────────────────────
 * Two earlier cuts asked for ONE number per row and computed the other bound —
 * first the band's start, then the band's top. Both were rejected as confusing,
 * for the same underlying reason: a field the user did not type is a field they
 * cannot check, and one of them was always locked or absent. The user's call is
 * that the ranges are theirs to enter and read back literally.
 *
 * So the ladder is NOT forced to be contiguous. Validation blocks only a band
 * that cannot mean anything — a negative number, a rate of 0 or less, or a To
 * below its own From. A hole or an overlap is permitted:
 *   • overlap → the lowest-starting matching band wins (deterministic)
 *   • hole    → no rate is quoted, and the payment screen says so rather than
 *               silently leaving whatever was in the Interest % box
 * See rateForLoanAmount in utils/pawnInterestCalculator.
 *
 * Controlled by PawnInterestSlabs, which owns the rows because the basis toggle
 * and the ladder are saved as one decision.
 */

const BLUE = "#2563EB";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const ERROR_C = "#DC2626";

const tfSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.82rem",
    bgcolor: "#fafbfc",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a5b4fc" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: BLUE,
      borderWidth: "1.5px",
    },
  },
  "& .MuiFormHelperText-root": { fontSize: "0.65rem", mx: 0.25 },
};

const headCellSx = {
  bgcolor: "#f8f9fc",
  color: INK_MUTED,
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.4px",
  textTransform: "uppercase",
  borderBottom: `1px solid ${BORDER}`,
  py: 1,
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  borderBottom: "1px solid #f0f2f5",
  py: 0.7,
  px: 1,
};

/** A blank band. The ladder always needs at least one. */
export const emptySlabRow = () => ({
  from_amount: "",
  to_amount: "",
  interest: "",
});

/**
 * Stored bands → editor rows. A straight copy: the fields are the fields, with
 * NULL (no upper limit) shown as a blank To.
 */
export const slabsToRows = (slabs) =>
  (slabs || [])
    .slice()
    .sort((a, b) => parseFloat(a.from_amount) - parseFloat(b.from_amount))
    .map((s) => ({
      from_amount: s.from_amount === null ? "" : parseFloat(s.from_amount),
      to_amount: s.to_amount === null ? "" : parseFloat(s.to_amount),
      interest: s.interest === null ? "" : parseFloat(s.interest),
    }));

/** Editor rows → the bands the API takes. Blank To goes as null. */
export const rowsToSlabs = (rows) =>
  (rows || []).map((r) => ({
    from_amount: Number(r.from_amount) || 0,
    to_amount: r.to_amount === "" ? null : Number(r.to_amount),
    interest: Number(r.interest),
  }));

/**
 * Ladder validation, exported so the screen and the save guard can never
 * disagree about what is wrong. Returns { [rowIndex]: { from?, to?, interest? } },
 * empty when the ladder is good.
 *
 * Only self-contradictory bands are rejected — see the note at the top of this
 * file on why gaps and overlaps are allowed through.
 */
export const validateSlabRows = (rows) => {
  const errors = {};
  const put = (i, field, msg) => {
    errors[i] = { ...(errors[i] || {}), [field]: msg };
  };

  rows.forEach((row, i) => {
    const from = parseFloat(row.from_amount);
    const to = row.to_amount === "" ? null : parseFloat(row.to_amount);
    const rate = parseFloat(row.interest);

    if (!Number.isFinite(from) || from < 0) put(i, "from", "Enter an amount");

    if (row.to_amount !== "") {
      if (!Number.isFinite(to) || to < 0) put(i, "to", "Enter an amount");
      else if (Number.isFinite(from) && to < from)
        put(i, "to", "Cannot be below From");
    }

    if (!Number.isFinite(rate) || rate <= 0) put(i, "interest", "Above 0");
  });

  return errors;
};

const PawnAmountSlabs = ({ rows = [], onRowsChange, errors = {} }) => {
  const setRow = (index, field, value) =>
    onRowsChange(
      rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    );

  const addRow = () => onRowsChange([...rows, emptySlabRow()]);

  const removeRow = (index) =>
    onRowsChange(rows.filter((_, i) => i !== index));

  // Negatives are refused at the keystroke, so the field can never hold one.
  const numericChange = (index, field) => (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setRow(index, field, "");
      return;
    }
    const n = parseFloat(raw);
    if (!Number.isNaN(n) && n >= 0) setRow(index, field, n);
  };

  const blockMinus = (e) => {
    if (e.key === "-" || e.key === "Minus" || e.key === "e" || e.key === "E")
      e.preventDefault();
  };

  const amountField = (i, field, placeholder, errKey) => (
    <TextField
      size="small"
      type="number"
      value={rows[i][field]}
      onChange={numericChange(i, field)}
      onKeyDown={blockMinus}
      error={!!(errors[i] || {})[errKey]}
      helperText={(errors[i] || {})[errKey] || ""}
      placeholder={placeholder}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Typography sx={{ fontSize: "0.8rem", color: INK_MUTED }}>
              ₹
            </Typography>
          </InputAdornment>
        ),
      }}
      inputProps={{ min: 0, step: 1 }}
      sx={{ ...tfSx, width: "100%", minWidth: 92 }}
    />
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Typography
        sx={{
          fontSize: "0.74rem",
          color: INK_MUTED,
          px: 1,
          pb: 0.75,
          lineHeight: 1.5,
          flexShrink: 0,
        }}
      >
        The rate is decided by the size of the loan. Enter the amount range each
        rate applies to. Both amounts are included; leave <b>To</b> blank for no
        upper limit.
      </Typography>

      <TableContainer
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, pl: 1 }}>From</TableCell>
              <TableCell sx={headCellSx}>To</TableCell>
              <TableCell sx={{ ...headCellSx, width: 108 }} align="center">
                Interest %
              </TableCell>
              <TableCell sx={{ ...headCellSx, width: 44 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => {
              const rowErr = errors[i] || {};
              return (
                <TableRow key={i} sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                  <TableCell sx={{ ...bodyCellSx, pl: 1 }}>
                    {amountField(i, "from_amount", "0", "from")}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {amountField(i, "to_amount", "No limit", "to")}
                  </TableCell>

                  <TableCell sx={bodyCellSx} align="center">
                    <TextField
                      size="small"
                      type="number"
                      value={row.interest}
                      onChange={numericChange(i, "interest")}
                      onKeyDown={blockMinus}
                      error={!!rowErr.interest}
                      helperText={rowErr.interest || ""}
                      placeholder="0.00"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography
                              sx={{ fontSize: "0.75rem", color: INK_MUTED }}
                            >
                              %
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{ min: 0, step: "any" }}
                      sx={{ ...tfSx, width: "100%", minWidth: 84 }}
                    />
                  </TableCell>

                  <TableCell sx={{ ...bodyCellSx, px: 0.5 }} align="center">
                    <Tooltip
                      title={
                        rows.length <= 1
                          ? "A ladder needs at least one band"
                          : "Remove this band"
                      }
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={rows.length <= 1}
                          onClick={() => removeRow(i)}
                          sx={{
                            color: ERROR_C,
                            bgcolor: "#fef2f2",
                            borderRadius: "7px",
                            width: 26,
                            height: 26,
                            "&:hover": { bgcolor: "#fee2e2" },
                            "&.Mui-disabled": {
                              bgcolor: "#f8fafc",
                              color: "#cbd5e1",
                            },
                          }}
                        >
                          <DeleteRoundedIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}

            {rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  align="center"
                  sx={{ py: 4, color: "#94a3b8", fontSize: "0.82rem" }}
                >
                  No bands yet — add one to start the ladder.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ flexShrink: 0, pt: 1, px: 1 }}>
        <Button
          size="small"
          startIcon={<AddRoundedIcon sx={{ fontSize: "16px !important" }} />}
          onClick={addRow}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.78rem",
            color: BLUE,
            borderRadius: "8px",
            px: 1.2,
            "&:hover": { bgcolor: "#eff6ff" },
          }}
        >
          Add slab
        </Button>
      </Box>
    </Box>
  );
};

export default PawnAmountSlabs;
